import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import Combobox from '../../components/base/Combobox';
import Input from '../../components/base/Input';
import { fetchProfessionals, type ProfessionalListItem } from '../../services/professionalsService';
import type { ServiceEngagementType } from '../../services/serviceEngagementService';
import { normalizeArabic } from '../../utils/arabicNormalize';
import { directoryEnabled, directoryTypeFor } from './engagementMeta';

export interface ProviderFieldProps {
  /** The input's id. The form focuses it when the professional is missing. */
  id: string;
  type: ServiceEngagementType | '';
  /** The chosen professional's AccountActivity id, '' when none. */
  value: string;
  onChange: (activityId: string) => void;
  /** i18n key of a validation message, shown in place of the hint. */
  error?: string | null;
}

interface DirectoryState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  items: ProfessionalListItem[];
}

/** The backend's page-size cap for GET /api/professionals. */
const DIRECTORY_PAGE_SIZE = 50;

const LABEL = 'mb-1.5 block text-[0.875rem] font-medium text-[var(--pv-color-text)]';
const LINK_BUTTON =
  'mt-1 inline-flex min-h-[2.75rem] items-center rounded-lg text-[0.875rem] font-bold text-[#1B7566] underline-offset-4 ' +
  'hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20 dark:text-[#7FD6C3]';

const searchText = (value: string) => normalizeArabic(value).toLowerCase();

/**
 * Who should do the work. With the professionals directory on and a service the
 * directory lists, this is a search over its VERIFIED professionals of that type
 * (GET /api/professionals, which only returns verified activities). Otherwise it
 * is the professional's activity id, typed in. A failed directory load falls
 * back to the id field rather than blocking the request.
 *
 * Mount it with `key={type}`: a new service starts from a clean search.
 */
export default function ProviderField({ id, type, value, onChange, error }: ProviderFieldProps) {
  const { t } = useTranslation('engage');
  const hintId = `${id}-hint`;
  const hasDirectory = directoryEnabled();
  const directoryType = hasDirectory && type ? directoryTypeFor(type) : null;

  const [manual, setManual] = useState(false);
  const [query, setQuery] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [directory, setDirectory] = useState<DirectoryState>({ status: 'idle', items: [] });

  useEffect(() => {
    if (!directoryType || manual) return;
    let cancelled = false;
    setDirectory({ status: 'loading', items: [] });
    fetchProfessionals({ activityType: directoryType, pageSize: DIRECTORY_PAGE_SIZE })
      .then((res) => {
        if (!cancelled) setDirectory({ status: 'ready', items: res.data ?? [] });
      })
      .catch(() => {
        if (!cancelled) setDirectory({ status: 'error', items: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [directoryType, manual, attempt]);

  const nameOf = (item: ProfessionalListItem) => item.displayName?.trim() || t('provider.unnamed');

  const options = useMemo(() => {
    const q = searchText(query.trim());
    if (!q) return directory.items;
    return directory.items.filter((item) => searchText(`${item.displayName ?? ''} ${item.city ?? ''}`).includes(q));
  }, [query, directory.items]);

  // The search shows while the directory is on and either no service is chosen
  // yet (disabled, "choose a service first") or the chosen one is listed.
  const picker = hasDirectory && !manual && directory.status !== 'error' && (type === '' || directoryType !== null);

  const useIdInstead = () => {
    setManual(true);
    setQuery('');
    onChange('');
  };

  const searchInstead = () => {
    setManual(false);
    setQuery('');
    onChange('');
    if (directory.status === 'error') {
      setDirectory({ status: 'loading', items: [] });
      setAttempt((n) => n + 1);
    }
  };

  if (picker) {
    const hint = error
      ? t(error)
      : directory.status === 'ready' && directory.items.length === 0
        ? t('provider.none')
        : null;
    return (
      <div>
        <Combobox<ProfessionalListItem>
          id={id}
          label={t('provider.label')}
          inputValue={query}
          onInputChange={(text) => {
            setQuery(text);
            if (value) onChange('');
          }}
          options={options}
          getOptionKey={(item) => item.id}
          renderOption={(item) => (
            <span className="flex min-w-0 flex-col">
              <span className="truncate">{nameOf(item)}</span>
              {item.city ? (
                <span className="truncate text-[0.875rem] text-[var(--pv-color-text-muted)]">{item.city}</span>
              ) : null}
            </span>
          )}
          onSelect={(item) => {
            onChange(item.id);
            setQuery(nameOf(item));
          }}
          onClear={() => onChange('')}
          selectedKey={value || null}
          loading={directory.status === 'loading'}
          emptyText={directory.items.length === 0 ? t('provider.none') : t('provider.noMatch')}
          placeholder={type ? t('provider.placeholder') : t('provider.chooseTypeFirst')}
          leadingIcon={<Search />}
          openOnFocus
          disabled={!type}
          invalid={Boolean(error)}
          inputProps={{ required: true, 'aria-describedby': hint ? hintId : undefined }}
        />
        {hint ? (
          <p
            id={hintId}
            className={`mt-1.5 text-[0.875rem] leading-relaxed ${
              error ? 'text-[#C62828] dark:text-[var(--pv-color-red)]' : 'text-[var(--pv-color-text-muted)]'
            }`}
          >
            {hint}
          </p>
        ) : null}
        {type ? (
          <button type="button" onClick={useIdInstead} className={LINK_BUTTON}>
            {t('provider.manualToggle')}
          </button>
        ) : null}
      </div>
    );
  }

  const hint = error
    ? t(error)
    : directory.status === 'error'
      ? t('provider.loadError')
      : t('provider.idHint');

  return (
    <div>
      <label htmlFor={id} className={LABEL}>
        {t('provider.idLabel')}
      </label>
      <Input
        id={id}
        dir="ltr"
        value={value}
        onChange={(event) => onChange(event.target.value.trim())}
        required
        autoComplete="off"
        spellCheck={false}
        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        invalid={Boolean(error)}
        aria-invalid={error ? true : undefined}
        aria-describedby={hintId}
      />
      <p
        id={hintId}
        className={`mt-1.5 text-[0.875rem] leading-relaxed ${
          error ? 'text-[#C62828] dark:text-[var(--pv-color-red)]' : 'text-[var(--pv-color-text-muted)]'
        }`}
      >
        {hint}
      </p>
      {directoryType ? (
        <button type="button" onClick={searchInstead} className={LINK_BUTTON}>
          {t('provider.directoryToggle')}
        </button>
      ) : null}
    </div>
  );
}
